import React, { useState, useEffect } from "react";
import { Input, DatePicker, Select, Table, Button } from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const CustomEditableTable = ({
  columnsConfig,
  tableData,
  output,
  setOutput,
  isLoading,
  handleAddRow,
  handleRemoveRow,
  setTableData,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  const onSelectChange = (newSelectedRowKeys, selectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys); // These are indices
    setSelectedRows(selectedRows); // These are the full row objects
  };

  const removeSelectedRows = () => {
    // Get labels from selected rows
    const labelsToRemove = selectedRows.map((row) => row.label);

    // Filter out selected rows
    const newTableData = tableData.filter(
      (row) => !labelsToRemove.includes(row.label)
    );

    // Update output by removing deleted rows
    if (setOutput && output) {
      const updatedOutput = { ...output };
      labelsToRemove.forEach((label) => {
        delete updatedOutput[label];
      });
      setOutput(updatedOutput);
    }

    // Optional callback for each removed row
    if (handleRemoveRow) {
      labelsToRemove.forEach((label) => {
        handleRemoveRow(label);
      });
    }

    // Update state
    setTableData(newTableData);
    setSelectedRowKeys([]);
    setSelectedRows([]);
  };

  useEffect(() => {
    const outputValues = Object.values(output || {});
    const requiredRows = outputValues.length;
    const currentRows = tableData.length;

    if (requiredRows > currentRows) {
      const newRows = Array.from({ length: requiredRows }, (_, index) => ({
        label: index + 1,
      }));
      setTableData(newRows);
    }
  }, [output]);

  const columns = columnsConfig?.map((col) => {
    const { title, dataIndex, key, type, valuePath, onChange, selectOptions } =
      col;

    return {
      title,
      dataIndex,
      key,
      render: (_, record) => {
        const value = valuePath(record, output);
        if (type === "text") {
          return (
            <p className="flex items-center m-0 pl-3 font-bold capitalize">
              {record?.label}
            </p>
          );
        }

        if (type === "input") {
          const inputType = col.inputType || "text"; // default to text
          return (
            <Input
              type={inputType}
              className="w-full custom-input"
              value={value}
              onChange={(e) => onChange(e.target.value, dataIndex, record)}
            />
          );
        }

        if (type === "textarea") {
          return (
            <TextArea
              className="w-full custom-input"
              value={value}
              onChange={(e) => onChange(e.target.value, dataIndex, record)}
            />
          );
        }

        if (type === "datePicker") {
          return (
            <DatePicker
              className="w-full custom-input"
              value={value && dayjs(value).isValid() ? dayjs(value) : null}
              onChange={(date, dateString) => {
                if (onChange) onChange(dateString, dataIndex, record);
              }}
            />
          );
        }

        if (type === "dateRange") {
          return (
            <RangePicker
              className="w-full custom-input"
              value={
                value?.length === 2
                  ? [dayjs(value[0]), dayjs(value[1])]
                  : [null, null]
              }
              onChange={(_, dateStrings) =>
                onChange(dateStrings, dataIndex, record)
              }
            />
          );
        }

        if (type === "select") {
          return (
            <Select
              className="w-full custom-input"
              value={value}
              onChange={(val) => onChange(val, dataIndex, record)}
              options={selectOptions || []}
            />
          );
        }

        return null;
      },
    };
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    preserveSelectedRowKeys: true,
    getCheckboxProps: (record) => ({
      name: record.label,
    }),
  };

  return (
    <div className="rounded-lg border overflow-hidden">
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        scroll={{ x: 900 }}
        rowKey={(record, index) => index}
        rowSelection={handleAddRow && rowSelection}
        loading={isLoading}
      />

      {handleAddRow && (
        <div className="p-2 text-right flex justify-end gap-2">
          {selectedRowKeys.length !== 0 && (
            <Button danger onClick={removeSelectedRows}>
              Remove
            </Button>
          )}

          <Button type="primary" onClick={handleAddRow}>
            Add More
          </Button>
        </div>
      )}
    </div>
  );
};

export default CustomEditableTable;
